import React, { useCallback, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';
import { Pagination } from '@videoblocks/storywind';

import RegionalSpinner from '../../../../common/components/RegionalSpinner';
import { areAllItemsVertical } from '../../../../common/stockItems/utils';
import {
  StockItem,
  StockItemContext,
  StockItemFormat,
} from '../../../../common/types/StockItemTypes';
import {
  filterUrlSeoSearchParameters,
  getPageUrl,
} from '../../../../common/utils';
import { JustifiedGallery } from '../../../Layouts/JustifiedGallery/JustifiedGallery';
import { VERTICAL_JUSTIFIED_GALLERY_HEIGHTS } from '../../../Layouts/JustifiedGallery/constants';
import { SearchPaginationState } from '../../SearchTypes';
import { AnySearchFilterOptions } from '../../containers/MenuContainerInterfaces';
import VideoStockItemCard from '../card/VideoStockItemCard';
import NoResults from '../noResults/NoResults';
import NumResultsViewed from '../numResults/NumResultsViewed';

export type StockItemDetails = {
  stockItem: StockItem;
  stockItemFormats: StockItemFormat[];
};

export interface VideoSearchResultsProps {
  currentStockItems: StockItemDetails[];
  searchLogParameters?: object;
  isLoading?: boolean;
  isLoadingAdditionalResults?: boolean;
  selectedStockItemDetails?: {
    stockItem: StockItem;
    stockItemFormats: StockItemFormat[];
  };
  goToSearchResultsPage?: (pageNumber: number) => void;
  redirectToSignUpLink: boolean;
  selectedSearchFilterOptions?: AnySearchFilterOptions;
  pagination?: SearchPaginationState;
  context: StockItemContext;
  shouldShowDownloadButton?: boolean;
  statusCode?: number;
  draggableCards?: boolean;
  countryCode?: string;
  hideSearchResults?: boolean;
  searchResultsCssClasses?: string;
  useNewDrawerStyle?: boolean;
  // Optionally provide OriginArea to use when triggering a click event from the rendered stock items.
  clickOriginArea?: OriginArea;
}

const VideoSearchResults = ({
  currentStockItems,
  searchLogParameters,
  isLoading,
  isLoadingAdditionalResults,
  selectedStockItemDetails,
  goToSearchResultsPage,
  redirectToSignUpLink,
  selectedSearchFilterOptions,
  pagination,
  context,
  shouldShowDownloadButton,
  statusCode,
  draggableCards,
  countryCode,
  hideSearchResults,
  searchResultsCssClasses,
  useNewDrawerStyle,
  clickOriginArea,
}: VideoSearchResultsProps) => {
  const MAX_SEARCH_PAGE_LIMIT = 99;
  const searchResultsContainerRef = useRef<HTMLDivElement>(null);
  const [hoveringGrid, setHoveringGrid] = useState(false);

  const getStockItemCardContext = useCallback(
    (title: string, position: number) => {
      return { title, position };
    },
    []
  );

  const getSearchResultsCSSClasses = useCallback(() => {
    const cssClasses = ['stock-item-group-wrapper', 'card-design', 'mt-5'];

    if (context === StockItemContext.CONTENT_TYPE) {
      cssClasses.push('media-type', 'mt-0');
    }

    return [twMerge(...cssClasses, searchResultsCssClasses)];
  }, [context, searchResultsCssClasses]);

  const getPortalContainer = (rowIndex: number) => {
    return typeof document !== 'undefined'
      ? (document.querySelector(
          `[data-portal-container-id="portal-container-${rowIndex}"]`
        ) as HTMLElement)
      : null;
  };

  const renderVideoStockItemCard = (
    stockItemDetails: StockItemDetails,
    index: number,
    drawerPortalContainer: HTMLElement
  ) => {
    return (
      <VideoStockItemCard
        key={stockItemDetails.stockItem.id}
        drawerPortalContainer={drawerPortalContainer}
        containPoppers={false}
        context={context}
        draggableCard={draggableCards || false}
        stockItem={stockItemDetails.stockItem}
        isSelected={
          selectedStockItemDetails &&
          stockItemDetails.stockItem.id ===
            selectedStockItemDetails.stockItem.id
        }
        page={null}
        hideOnThreeColumnLayout={false}
        searchLogParameters={searchLogParameters || {}}
        shouldShowDownloadButton={shouldShowDownloadButton}
        shouldStockItemHaveNewFlag={
          stockItemDetails.stockItem.shouldStockItemHaveNewFlag
        }
        stockItemCardContext={getStockItemCardContext(context, index + 1)}
        stockItemFormats={stockItemDetails.stockItemFormats}
        redirectToSignUpLink={redirectToSignUpLink}
        clickOriginArea={clickOriginArea}
        videoStyles={{ width: '100%', height: '100%' }}
      />
    );
  };

  const renderJustifiedGallery = () => {
    // Check if all items are vertical
    const allItemsAreVertical = areAllItemsVertical(currentStockItems);

    const responsiveHeights = allItemsAreVertical
      ? VERTICAL_JUSTIFIED_GALLERY_HEIGHTS
      : undefined;

    return (
      <JustifiedGallery
        items={currentStockItems.map((stockItemDetails) => ({
          id: stockItemDetails.stockItem.id,
          aspectRatio: stockItemDetails.stockItem.aspectRatio,
        }))}
        shouldRenderPortalContainer={hoveringGrid}
        onMouseEnter={() => setHoveringGrid(true)}
        responsiveHeights={responsiveHeights}
      >
        {({ index, rowIndex }) => {
          const stockItemDetails = currentStockItems[index];
          return renderVideoStockItemCard(
            stockItemDetails,
            index,
            getPortalContainer(rowIndex)
          );
        }}
      </JustifiedGallery>
    );
  };

  const renderPagination = useCallback(() => {
    if (!pagination) return null;

    const { resultsPerPage, totalResults, maxPageNumber, currentPageNumber } =
      pagination;
    const showLoadingIndicator = isLoading && isLoadingAdditionalResults;
    const totalPages = Math.min(
      pagination.maxPageNumber,
      MAX_SEARCH_PAGE_LIMIT
    );

    // In the case that a page is access through query params, don't show pagination
    if (currentPageNumber > maxPageNumber) {
      return null;
    }

    if (showLoadingIndicator) {
      return (
        <div className="search-pagination text-center">
          <RegionalSpinner
            className="pagination-spinner"
            width="80px"
            countryCode={countryCode}
          />
        </div>
      );
    }

    const getPageHref = (pageNumber: number) => {
      const isFirstPage = pageNumber === 1;
      const url = filterUrlSeoSearchParameters(
        getPageUrl(isFirstPage ? undefined : pageNumber)
      );
      return url;
    };

    return (
      <div className="search-pagination text-center">
        <NumResultsViewed
          total={
            selectedSearchFilterOptions?.similarTo &&
            totalResults > resultsPerPage
              ? resultsPerPage
              : totalResults
          }
        />
        <Pagination
          activePage={pagination.currentPageNumber}
          onPageSelect={goToSearchResultsPage}
          prefix="search-results"
          totalPages={totalPages}
          getHref={getPageHref}
          terminalButtons={2}
          maxButtons={9}
        />
      </div>
    );
  }, [
    pagination,
    isLoading,
    isLoadingAdditionalResults,
    countryCode,
    selectedSearchFilterOptions,
    goToSearchResultsPage,
  ]);

  const renderLoadingSpinner = useCallback(() => {
    return (
      <div className="spinner-container justify-center">
        <RegionalSpinner
          className="search-results-spinner"
          countryCode={countryCode}
        />
      </div>
    );
  }, [countryCode]);

  const renderNoResults = useCallback(() => {
    return <NoResults searchParams={selectedSearchFilterOptions} />;
  }, [selectedSearchFilterOptions]);

  const renderApiError = useCallback(() => {
    const httpStatusCode = statusCode;
    let errMessage =
      'Oops, we are having trouble understanding your search. Please try something simpler.';

    if (httpStatusCode >= 500) {
      errMessage =
        'Well this is embarrassing… our search engine blew a fuse. Please try your search again later.';
    } else if (httpStatusCode === 408) {
      errMessage =
        'Well this is embarrassing… our search engine blew a fuse. Please try your search again or refresh the page.';
    }

    return (
      <div className="as-no-results">
        <p>{errMessage}</p>
      </div>
    );
  }, [statusCode]);

  // Main render logic
  const shouldRenderLoading =
    (isLoading && !isLoadingAdditionalResults) || isLoadingAdditionalResults;

  if (shouldRenderLoading) {
    return renderLoadingSpinner();
  }

  if (statusCode && statusCode !== 200) {
    return renderApiError();
  }

  if (hideSearchResults || pagination?.totalResults <= 0) {
    return renderNoResults();
  }

  return (
    <section
      className={twMerge(getSearchResultsCSSClasses())}
      ref={searchResultsContainerRef}
    >
      <div
        id="as-search-results"
        className={useNewDrawerStyle ? 'relative' : ''}
      >
        {renderJustifiedGallery()}
      </div>
      {pagination?.totalResults > 0 && renderPagination()}
    </section>
  );
};

export default VideoSearchResults;
